import { EDITOR_CONSTANTS } from "@perceptron/constants/editorConstants";
import { MediaSetContext } from "@perceptron/utils/types";
import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";

import { handleFilePaste } from "@demo/utils/file-handler";

import { replaceRangeAroundProtectedNodes } from "./utils/node-protection";

export interface ClipboardGuardOptions {
  context: MediaSetContext;
}

// Intercepts paste events that include image/video files and prevents default HTML paste
export const ClipboardGuardExtension = Extension.create<ClipboardGuardOptions>({
  name: "clipboardGuard",
  priority: 10000,

  addOptions() {
    return {
      context: MediaSetContext.DOCUMENT_EDITOR,
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const { context } = this.options;

    return [
      new Plugin({
        props: {
          handlePaste: (view, event, _slice) => {
            const dt = event?.clipboardData;
            if (!dt) return false;

            // Check for files first (images/videos)
            const allFiles = Array.from(dt.files || []);
            if (allFiles.length > 0) {
              // Handle file paste
              event.preventDefault();
              handleFilePaste(context, editor, allFiles);
              return true;
            }

            // For text pastes, strip all formatting and insert as plain text
            const text = dt.getData("text/plain");
            if (text) {
              event.preventDefault();

              // When the selection spans a protected media container, delete
              // around it and insert there — a plain insertText would either
              // be blocked by node protection or (for a whole-doc selection)
              // replace the container outright.
              const { from, to } = view.state.selection;
              const handledAroundProtectedNodes = replaceRangeAroundProtectedNodes(view, from, to, text, [
                { nodeName: EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER },
              ]);

              if (!handledAroundProtectedNodes) {
                // Insert plain text at current position
                const { tr } = view.state;
                tr.insertText(text);
                view.dispatch(tr);
              }

              return true; // We handled the paste
            }

            return false;
          },
        },
      }),
    ];
  },
});
